import { useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { useT } from "../settings/context";

/**
 * THE BUTTON, including what it looks like while it is working.
 *
 * One component rather than a `busy ? "…" : label` in every screen: this state
 * will appear on sign-in, on registration, on payment, on every screen that
 * waits for the server. Written once, it behaves the same everywhere — and it
 * gets the awkward parts right once instead of never.
 *
 * ⚠️ THE LABEL DOES NOT CHANGE. Swapping it for "Connexion…" resized the button
 * mid-click, which moves whatever sits under it, and forced a second string to
 * write and translate for every action in the product. The spinner says
 * "working"; the label keeps saying what is being done.
 *
 * Waiting is announced in words as well as drawn: a spinner tells a screen reader
 * nothing, and `aria-busy` on a button is announced by almost none of them. A
 * live region does the job the animation does for everyone else.
 *
 * Displayed strings are French: this is a French product.
 */
export function Button({
  loading = false,
  variant,
  children,
  ...rest
}: {
  loading?: boolean;
  /** `ghost` for a secondary action; omitted for the main one. */
  variant?: "ghost";
  children: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const t = useT();
  const [hasFocus, setHasFocus] = useState(false);
  /* Inactif pendant qu'il travaille : un second clic enverrait une seconde requête, et
     c'est sur une connexion lente qu'on reclique. */
  const blocked = loading || !!rest.disabled;
  /**
   * ⚠️ MAIS PAS `disabled` TANT QU'IL A LE FOCUS (bug #406). Un élément focalisé qui
   * devient `disabled` rend le focus au `body` : au clavier, « Enregistrer » ou
   * « Envoyer » renvoyait en haut de la page, et le lecteur d'écran perdait sa place.
   * Pendant le chargement, et pendant un délai posé alors qu'on est dessus (le compte
   * à rebours de la connexion), le bouton reste donc focalisable : `aria-disabled` le
   * dit inactif, et le clic est ignoré ici. Une fois quitté, il redevient `disabled`
   * et sort de l'ordre de tabulation.
   */
  const nativeDisabled = !!rest.disabled && !loading && !hasFocus;
  return (
    <button
      type="button"
      {...rest}
      className={`btn${variant === "ghost" ? " btn--ghost" : ""}${rest.className ? ` ${rest.className}` : ""}`}
      disabled={nativeDisabled}
      aria-disabled={blocked || undefined}
      aria-busy={loading || undefined}
      onFocus={(e) => {
        setHasFocus(true);
        rest.onFocus?.(e);
      }}
      onBlur={(e) => {
        setHasFocus(false);
        rest.onBlur?.(e);
      }}
      onClick={(e) => {
        /* `preventDefault` aussi : sur un bouton `submit`, c'est ce qui empêche le
           formulaire de partir, Entrée dans un champ compris. */
        if (blocked) return e.preventDefault();
        rest.onClick?.(e);
      }}
    >
      {loading && <span className="spin" aria-hidden="true" />}
      {children}
      {loading && (
        <span className="a11y" role="status">
          {t("IN_PROGRESS", 'En cours')}
        </span>
      )}
    </button>
  );
}
